import { UploadOutlined } from "@ant-design/icons";
import { useApiUrl, useCustomMutation, useNotification } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Input,
  Space,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import dayjs from "dayjs";
import Papa from "papaparse";
import React from "react";
import { ExportButtons } from "./components/ExportButtons";
import { FilterBar } from "./components/FilterBar";
import { VesselConsumptionTable } from "./components/VesselConsumptionTable";
import { VesselDetailDrawer } from "./components/VesselDetailDrawer";
import { parseImoInput } from "./lib/imo-parsing";
import {
  formatPeriodLabel,
  isSamePeriod,
  Period,
  periodQueryParams,
} from "./lib/period";
import { ImoReportResult, VesselConsumption } from "./lib/types";

// The generated report is a snapshot: it must keep exporting/emailing the
// exact IMO list (and period) that was POSTed, even if the textarea/period is
// edited afterward. `imos`/`period` here are that snapshot, not a derived
// view of the live input.
interface ImoReportSnapshot extends ImoReportResult {
  imos: string[];
  period: Period;
}

export const FlatFeeImoReportPage: React.FC = () => {
  const apiUrl = useApiUrl();
  const { open: notify } = useNotification();

  const [rawInput, setRawInput] = React.useState("");
  const [period, setPeriod] = React.useState<Period>({
    mode: "year",
    year: dayjs().year(),
  });
  const [services, setServices] = React.useState<string[]>(["AVCS"]);
  const [reportResult, setReportResult] =
    React.useState<ImoReportSnapshot | null>(null);
  const [drawerVessel, setDrawerVessel] =
    React.useState<VesselConsumption | null>(null);

  const parsed = React.useMemo(() => parseImoInput(rawInput), [rawInput]);

  const {
    mutate: generateReport,
    mutation: { isPending: isGenerating },
  } = useCustomMutation();

  const handleGenerateReport = () => {
    // Snapshot the exact list + period being POSTed now - the textarea/period
    // (and `parsed` derived from the textarea) can keep changing while the
    // request is in flight or after it resolves, but the report/export scope
    // must stay pinned to what was actually requested.
    const imosUsed = parsed.valid;
    const periodUsed = period;
    generateReport(
      {
        url: `${apiUrl}/flat-fee-analytics/imo-report`,
        method: "post",
        values: { imos: imosUsed, ...periodQueryParams(periodUsed) },
      },
      {
        onError: (error: any) => {
          const message = error?.response?.data?.message;
          notify?.({
            type: "error",
            message: "Report Failed",
            description:
              (Array.isArray(message) ? message.join(", ") : message) ||
              error?.message ||
              "An error occurred while generating the report.",
          });
        },
        onSuccess: (data) => {
          const result = data?.data as unknown as ImoReportResult;
          setReportResult({ ...result, imos: imosUsed, period: periodUsed });
        },
      },
    );
  };

  // Cheap staleness check: has the parsed valid-IMO list or the period drifted
  // from the snapshot the visible report was generated from?
  const isInputStale =
    reportResult !== null &&
    (parsed.valid.length !== reportResult.imos.length ||
      parsed.valid.join(",") !== reportResult.imos.join(",") ||
      !isSamePeriod(period, reportResult.period));

  const uploadProps: UploadProps = {
    accept: ".csv",
    showUploadList: false,
    beforeUpload: (file) => {
      Papa.parse(file, {
        complete: (results) => {
          const firstColumn = (results.data as string[][])
            .map((row) => row?.[0] ?? "")
            .join("\n");
          setRawInput((prev) =>
            prev ? `${prev}\n${firstColumn}` : firstColumn,
          );
        },
      });
      return false;
    },
  };

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Input.TextArea
              rows={6}
              placeholder="Paste IMO numbers separated by commas, spaces, or newlines"
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
            />
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Upload CSV</Button>
            </Upload>

            {parsed.truncated && (
              <Alert
                type="warning"
                showIcon
                message={`IMO list truncated to ${parsed.valid.length}`}
              />
            )}

            {parsed.invalid.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${parsed.invalid.length} invalid IMO token(s) will be ignored`}
                description={
                  <Collapse
                    size="small"
                    ghost
                    items={[
                      {
                        key: "invalid-tokens",
                        label: "Show invalid tokens",
                        children: parsed.invalid.join(", "),
                      },
                    ]}
                  />
                }
              />
            )}

            <FilterBar
              period={period}
              onPeriodChange={setPeriod}
              services={services}
              onServicesChange={setServices}
            />

            <Button
              type="primary"
              loading={isGenerating}
              disabled={parsed.valid.length === 0}
              onClick={handleGenerateReport}
            >
              Generate report
            </Button>
          </Space>
        </Card>

        {reportResult && (
          <Card>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {isInputStale && (
                <Alert
                  type="info"
                  showIcon
                  message="Input changed since this report was generated. Click Generate report to refresh."
                />
              )}

              {reportResult.unmatchedImos.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={`${reportResult.unmatchedImos.length} IMO number(s) did not match a vessel`}
                  description={reportResult.unmatchedImos.join(", ")}
                />
              )}

              <Typography.Text type="secondary">
                Report period: {formatPeriodLabel(reportResult.period)}
              </Typography.Text>

              <div style={{ textAlign: "right" }}>
                <ExportButtons
                  scope={{ scopeType: "imo-list", imos: reportResult.imos }}
                  period={reportResult.period}
                />
              </div>

              <VesselConsumptionTable
                vessels={reportResult.vessels}
                loading={isGenerating}
                onVesselClick={setDrawerVessel}
              />
            </Space>
          </Card>
        )}
      </Space>

      <VesselDetailDrawer
        vessel={drawerVessel}
        period={reportResult?.period ?? period}
        onClose={() => setDrawerVessel(null)}
      />
    </>
  );
};
